import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useInstitutionAdminCheck } from "@/hooks/useInstitutionAdminCheck";
import { useSchoolAdminCheck } from "@/hooks/useSchoolAdminCheck";
import { useInstitutionViewerCheck } from "@/hooks/useInstitutionViewerCheck";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

const LOADING_TIMEOUT = 12000;

/**
 * InstitutionRoute protects the admin cohort dashboard.
 * Only admins, institution admins, school admins, and institution viewers can access.
 * Institutional students are NOT allowed here — they use /dashboard instead.
 */
export const InstitutionRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { isInstitutionAdmin, loading: instAdminLoading } = useInstitutionAdminCheck();
  const { isSchoolAdmin, loading: schoolAdminLoading } = useSchoolAdminCheck();
  const { isInstitutionViewer, loading: viewerLoading } = useInstitutionViewerCheck();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const allLoading = authLoading || adminLoading || instAdminLoading || schoolAdminLoading || viewerLoading;

  // Only admin roles and viewers — NOT institutional students
  const hasAccess = isAdmin || isInstitutionAdmin || isSchoolAdmin || isInstitutionViewer;

  useEffect(() => {
    if (!allLoading) {
      setTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setTimedOut(true), LOADING_TIMEOUT);
    return () => clearTimeout(timer);
  }, [allLoading]);

  useEffect(() => {
    if (allLoading) return;

    if (!user) {
      navigate("/auth/login", { replace: true });
      return;
    }

    if (hasAccess) {
      setIsReady(true);
    } else {
      // Institutional students or unauthorized users → send to /dashboard silently
      navigate("/dashboard", { replace: true });
    }
  }, [user, hasAccess, allLoading, navigate, t]);

  if (allLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t("common.loading")}</p>
          {timedOut && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4 gap-2"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="w-4 h-4" />
              {t("common.retry") || "Reintentar"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return isReady ? <>{children}</> : null;
};
